/**
 * STANDARD CYCLE TIME SUMMARY — one row per SAP code per SEASON.
 *
 * Until 2026-09-08 this was a QUERY: every active OPA document exploded over the SAP codes in its
 * SKUs field, with the cycle times computed from the document's own operation lines. Max replaced
 * that model — "Phần này trước là chúng ta lấy từ SKU điền trong OPA. Giờ tôi cần xây lại là có sẵn
 * cái bảng SKU từ Style hub xong rồi user sẽ điền tay hoặc import vào."
 *
 * So the rows are STORED now, keyed on (season, SAP code), and the season workbook is imported rather
 * than reverse-engineered. Line / Category / Style Name / Colorway still come from Style Hub, because
 * that is where they come from; everything else is entered.
 *
 * The old model is not gone by accident: the PT team's own S27 workbook and the portal's computed
 * summary were compared before the switch, and on all 182 SKUs they both had, every SCT value agreed.
 * The workbook simply had 45 more.
 *
 * WHY AN IMPORT NEVER OVERWRITES: a season's measured cycle times are somebody's month of work. A
 * duplicate is reported before anything is written, and refused after, with the collisions handed
 * back as a workbook (Max: "Nếu import trùng thì sẽ cảnh báo cho user trước ... Những cái trùng sẽ
 * gửi trả về user qua file đã được highlight").
 *
 * The indirect rate is shown as HOURS, not a percentage, the way the workbook shows it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { FileDown, RefreshCw, ExternalLink, FilterX, Upload, AlertTriangle,
         Pencil, Trash2, Eye } from 'lucide-react';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import useFrozenColumns from './useFrozenColumns';

/* THE COLUMNS, IN THE PT TEAM'S OWN ORDER — Max, 2026-09-13: "phần SCT có format mới nhờ bạn xem
   điều chỉnh lại nhé", with HELINOX - S27 STANDARD CYCLE TIME SUMMARY 20260911.xlsx.

   Their sheet reads left to right as the calculation runs: who the row is, then the two subtotals,
   then the cycles those subtotals are made of, then efficiency and the indirect rate, and SCT last
   as the answer. It used to put SCT third, which reads as "here is the number, and here is some
   detail" — the opposite way round from how the number is arrived at.

   Two columns are new and neither is stored: ΣVariable and ΣStandard are SUMS of the cells beside
   them (their file holds =SUM(L:R) and =SUM(S:U)). Storing a total next to its own parts gives a row
   two answers to one question. PRODUCTION NAME is not new — it has been on every row since the
   import and was simply never shown. */
const COLUMNS = [
  /* Season first and frozen: the table holds every season at once now, so which one a row belongs to
     is the first thing about it (Max, 2026-09-08: "Bảng theo mùa nên cần thêm cột season"). */
  { key: 'season', label: 'Season' },
  { key: 'line', label: 'Line' },
  { key: 'category', label: 'Category' },
  { key: 'sap', label: 'SAP Code' },
  { key: 'styleName', label: 'Style Name', cls: 'sct-style' },
  { key: 'productionName', label: 'Production Name', cls: 'sct-style' },
  { key: 'colorway', label: 'Colorway' },
  { key: 'frameColor', label: 'Frame Color' },
  { key: 'varTotal', label: 'ΣVariable Sewing Cycles', num: true, derived: true },
  { key: 'stdTotal', label: 'ΣStandard Work Subtotal', num: true, derived: true },
  { key: 'skin', label: 'Skin CT (h)', num: true },
  { key: 'caseH', label: 'Case CT (h)', num: true },
  { key: 'acc1', label: 'ACC1 CT (h)', num: true },
  { key: 'acc2', label: 'ACC2 CT (h)', num: true },
  { key: 'acc3', label: 'ACC3 CT (h)', num: true },
  { key: 'acc4', label: 'ACC4 CT (h)', num: true },
  { key: 'acc5', label: 'ACC5 CT (h)', num: true },
  { key: 'frameFit', label: 'Frame-Fit CT (h)', num: true },
  { key: 'frameAss', label: 'Frame ass. CT (h)', num: true },
  { key: 'packaging', label: 'Packaging CT (h)', num: true },
  { key: 'efficiency', label: 'Style Efficiency', num: true },
  { key: 'indirectHours', label: 'Indirect Rate (h)', num: true },
  { key: 'sct', label: 'SCT (h)', num: true },
  /* OPA stays (Max, 2026-09-02), and their file puts it last: nothing is computed from it any more —
     it is a reference back to the document the numbers were measured on. */
  { key: 'docNo', label: 'OPA', cls: 'sct-opa' },
];

/* The two subtotals, worked out the way their formulas do — ROUNDed like every other cycle figure in
   this model, so the column adds up to what the SCT line was computed from. */
const round2 = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;
const sumOf = (row, keys) => {
  const vals = keys.map((k) => row[k]).filter((v) => v !== null && v !== undefined && v !== '');
  if (!vals.length) return null;
  return round2(vals.reduce((a, v) => a + Number(v), 0));
};
export const withTotals = (row) => ({
  ...row,
  varTotal: sumOf(row, ['skin', 'caseH', 'acc1', 'acc2', 'acc3', 'acc4', 'acc5']),
  stdTotal: sumOf(row, ['frameFit', 'frameAss', 'packaging']),
});
/* Line, Category, SAP Code and Style Name freeze to the left (Max, 2026-08-30) — reading across to
   Packaging is useless if you have lost which SKU the row is. Sticky needs a real offset, so the
   widths are declared here and used by both the header and the body; measuring live widths would
   have the two disagree mid-scroll. Style Name and Production Name are the two that hold sentences,
   and they were the two squeezed hardest, so they get the room. */
/* Five frozen columns: No, Line, Category, SAP Code, Style Name. The widths are what they ask for;
   the offsets come from useFrozenColumns, which measures what they got. */
/* Six frozen columns now: No, OPA, Line, Category, SAP Code, Style Name. The freeze still ends at
   Style Name, which is where it has to end for a row to stay identifiable. */
/* The freeze follows the new order: OPA moved to the end with the file's layout, so the block that
   keeps a row identifiable is Season · Line · Category · SAP Code · Style Name. */
const FROZEN_KEYS = ['season', 'line', 'category', 'sap', 'styleName'];
const FROZEN = { season: 90, line: 116, category: 140, sap: 108, styleName: 240 };
/* Every column has a declared width, because the table is a FIXED layout now. Under `auto` a column
   took whatever its content asked for, so the widths the sticky offsets were computed from were not
   the widths that rendered — and the frozen columns ended up a few pixels apart, with the scrolling
   rows visible through the gap (Max, 2026-09-08: "khi scroll phải có cảm giác có chữ chạy phía
   dưới"). Fixed layout makes the colgroup binding, which is what makes the offsets exact. */
/* The three standard-work columns carry the longest headers on the table — "Frame ass. CT (h)" is
   17 characters — and at the default 104 they were clipping their own labels (Max, 2026-09-09). */
/* Measured, not guessed: the width each header's TEXT needs plus its funnel and the cell padding,
   read off the rendered page. Fourteen of the twenty were a few pixels short and clipping their own
   labels — including every CT column at the shared default (Max, 2026-09-09). */
const FREE_W = {
  productionName: 210, colorway: 150, frameColor: 150, sct: 100, efficiency: 148, indirectHours: 136,
  frameFit: 148, frameAss: 156, packaging: 150,
  // The two Σ headers are the longest on the table — "ΣVariable Sewing Cycles" is 23 characters.
  varTotal: 190, stdTotal: 190, docNo: 100,
};
const DEFAULT_W = 120;
/* Measured off the rendered buttons, not estimated: Edit 64 + Delete 78 + the gap + the cell's own
   padding. The Action column is pinned to the RIGHT, so anything short of this puts a button under
   the scrolling content instead of over it. View (2026-10-16) adds ~64 + a gap. */
const ACTION_W = 240;
const COL_W = (key) => FROZEN[key] || FREE_W[key] || DEFAULT_W;

/* A translated sentence with an element inside it (a bold count, a bold file name): the placeholder is
   filled with a marker, the string split on it and the element dropped in, so each language keeps its
   own word order. */
// The markers are control characters on purpose: they cannot occur in any translation.
// eslint-disable-next-line no-control-regex
const fillParts = (text, parts) => text.split(/(\u0001\w+\u0002)/).map((piece, i) => {
  // eslint-disable-next-line no-control-regex
  const m = piece.match(/^\u0001(\w+)\u0002$/);
  // eslint-disable-next-line react/no-array-index-key
  return m ? <React.Fragment key={i}>{parts[m[1]]}</React.Fragment> : piece;
});
const marker = (name) => `\u0001${name}\u0002`;

/* The workbook's two-tier header, kept because it is how the PT team reads the sheet: the five
   variable sewing cycles under one banner, the three standard-work ones under another. */
const GROUPS = [
  // The workbook's banner covers five because it stops at ACC3; ACC4 and ACC5 are kept here
  // (Max, 2026-09-08: "vẫn giữ đến acc 5"), so the same banner covers seven.
  { from: 'skin', span: 7, label: 'Variable Sewing Cycles' },
  { from: 'frameFit', span: 3, label: 'Standard Work Subtotal' },
];

/* Style efficiency as a WHOLE percent, rounded half away from zero — 64.5 goes up, not to even
   (Max, 2026-09-03: "làm tròn theo nguyên tắc quá bán"). The stored value keeps every decimal it
   has; this is only how the column reads.

   `.toFixed(0)` is not this. It rounds the BINARY number, and some halves do not survive the trip:
   0.145 * 100 is 14.499999999999998, which toFixed prints as 14 where the decimal says 15. Rare —
   two values in a thousand — but it is the same trap opaRound documents for the cycle times, and the
   fix is the same: trim to 12 significant digits so the decimal is restored before rounding. */
const pctWhole = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const scaled = Number((Math.abs(n) * 100).toPrecision(12));
  return (n < 0 ? -1 : 1) * Math.round(scaled);
};

const GETTERS = Object.fromEntries(COLUMNS.map((c) => [
  c.key,
  /* Efficiency sorts and filters on the ROUNDED percent, so the column menu offers one "65" rather
     than three buckets — 0.649, 0.650, 0.653 — that every row displays identically. Same reasoning
     as slicing a timestamp to the day before it reaches a filter list. */
  // eslint-disable-next-line no-nested-ternary
  c.key === 'efficiency' ? (row) => (pctWhole(row.efficiency) ?? 0)
    : (c.num ? (row) => Number(row[c.key] ?? 0) : (row) => row[c.key] || ''),
]));

/* A cycle that does not apply to a style is BLANK in the workbook, and it is blank here. It used to
   print 0.00 because the old model computed every section for every row; a stored null means "nobody
   measured this", which is not the same as "it takes no time" and should not read like it. */
const n2 = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))
  ? '' : Number(v).toFixed(2));
const pct = (v) => {
  const w = pctWhole(v);
  return w === null ? '' : `${w}%`;
};

export default function SctSummary({ apiBase }) {
  const { t } = useTranslation();
  /* The headings ON SCREEN, in the reader's language. COLUMNS keeps the English, which is also what a
     column with no translation (Skin / Case / ACC CT, SCT, OPA) shows. The import template and the
     export are built on the server from their own header map and are not touched by this. */
  const COL_LABEL = {
    season: t('xSct.col.season'), line: t('xSct.col.line'), category: t('xSct.col.category'),
    sap: t('xSct.col.sap'), styleName: t('xSct.col.styleName'), productionName: t('xSct.col.productionName'),
    colorway: t('xSct.col.colorway'), frameColor: t('xSct.col.frameColor'),
    varTotal: t('xSct.col.varTotal'), stdTotal: t('xSct.col.stdTotal'),
    frameFit: t('xSct.col.frameFit'), frameAss: t('xSct.col.frameAss'), packaging: t('xSct.col.packaging'),
    efficiency: t('xSct.col.efficiency'), indirectHours: t('xSct.col.indirectHours'),
  };
  const GROUP_LABEL = { skin: t('xSct.groupVar'), frameFit: t('xSct.groupStd') };
  const labelOf = (key, fallback) => COL_LABEL[key] || fallback;
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  /* The columns a person may change. Everything else on the row is Style Hub's — the identity block
     is shown in the dialog so the row is recognisable, and shown read-only so nobody edits a name
     here that SAP owns and the next refresh would put back. */
  const EDITABLE = [
    { key: 'sct', label: 'SCT (h)' }, { key: 'efficiency', label: labelOf('efficiency', 'Style Efficiency'), pct: true },
    { key: 'indirectHours', label: labelOf('indirectHours', 'Indirect Rate (h)') },
    { key: 'skin', label: 'Skin CT (h)' }, { key: 'caseH', label: 'Case CT (h)' },
    { key: 'acc1', label: 'ACC1 CT (h)' }, { key: 'acc2', label: 'ACC2 CT (h)' },
    { key: 'acc3', label: 'ACC3 CT (h)' }, { key: 'acc4', label: 'ACC4 CT (h)' },
    { key: 'acc5', label: 'ACC5 CT (h)' },
    { key: 'frameFit', label: labelOf('frameFit', 'Frame-Fit CT (h)') },
    { key: 'frameAss', label: labelOf('frameAss', 'Frame ass. CT (h)') },
    { key: 'packaging', label: labelOf('packaging', 'Packaging CT (h)') },
  ];
  const [edit, setEdit] = useState(null);       // { row, form }
  const [opaQuery, setOpaQuery] = useState(null);   // null = not searching, string = the typed filter
  const [drop, setDrop] = useState(null);       // the row awaiting confirmation
  const [seasons, setSeasons] = useState([]);
  const [opaDocs, setOpaDocs] = useState([]);
  const [season, setSeason] = useState('');
  /* The import is two steps and the middle one is a dialog: what the file holds, which season it is
     for, and exactly which codes would collide — before a single row is written. */
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  /* VIEW — Jay, 2026-10-16 ("Same for all similar function"): the same dialog, every box locked, Close. */
  const openEdit = (row, view = false) => { setOpaQuery(null); setEdit({
    row,
    view,
    // Efficiency is stored as a fraction and read as a percent, so the BOX shows the percent.
    form: Object.fromEntries(EDITABLE.map((f) => [f.key,
      row[f.key] == null ? '' : String(f.pct ? pctWhole(row[f.key]) ?? '' : row[f.key])])),
    opaDocNo: row.docNo || '',
  }); };

  const saveEdit = async () => {
    if (!edit) return;
    setBusy(true); setError('');
    try {
      const body = { opaDocNo: edit.opaDocNo };
      EDITABLE.forEach((f) => {
        const raw = String(edit.form[f.key] ?? '').trim();
        // '' means "nobody measured this" and must reach the server as null, not 0.
        body[f.key] = raw === '' ? null : (f.pct ? Number(raw) / 100 : Number(raw));
      });
      const response = await fetch(`${apiBase}/opa/sct/${edit.row.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xSct.errSave'));
      setRows((data.rows || []).map(withTotals));
      setEdit(null);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!drop) return;
    setBusy(true); setError('');
    try {
      const response = await fetch(`${apiBase}/opa/sct/${drop.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xSct.errDelete'));
      setRows((data.rows || []).map(withTotals));
      setDrop(null);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const exportXlsx = async () => {
    setBusy(true); setError('');
    try {
      const response = await fetch(`${apiBase}/opa/sct/export`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ season, sapCodes: shown.map((r) => r.sap) }),
      });
      if (!response.ok) {
        throw new Error((await response.json().catch(() => ({}))).error || t('xSct.errExport'));
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `HEVN_SCT_${season}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const [tableRef, frozenLeft] = useFrozenColumns(FROZEN_KEYS.length + 1,
    [rows.length, search, sort, filters, season]);

  const load = useCallback(async (wanted) => {
    setBusy(true); setError('');
    try {
      const q = wanted ? `?season=${encodeURIComponent(wanted)}` : '';
      const response = await fetch(`${apiBase}/opa/sct${q}`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || i18n.t('xSct.errLoad'));
      setSeasons(data.seasons || []);
      setOpaDocs(data.opaDocs || []);
      setSeason(data.season || '');
      setRows((data.rows || []).map(withTotals));
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }, [apiBase]);
  useEffect(() => { load(); }, [load]);

  /* Step one: nothing is written. The server reports which season it read the file as, which SHEET it
     read — the workbook carries four working drafts beside the summary — and every collision. */
  const pickFile = async (file) => {
    if (!file) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const buf = await file.arrayBuffer();
      let bin = '';
      new Uint8Array(buf).forEach((b) => { bin += String.fromCharCode(b); });
      /* The season is NOT sent. It used to carry whichever one the picker was showing, and the
         server treated that as the answer — so a file with 2026 written on a row was imported into
         2027 because that is what the screen happened to be on, and came back reported as a
         duplicate (Max, 2026-09-09: "tui import season khác mà nó vẫn báo lỗi"). Each row's own
         Season column decides; the file name is the fallback for rows that leave it blank. */
      const body = { fileName: file.name, contentBase64: btoa(bin) };
      const response = await fetch(`${apiBase}/opa/sct/import`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xSct.errRead'));
      setPlan({ ...data, body });
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const runImport = async () => {
    if (!plan) return;
    setBusy(true); setError('');
    try {
      const response = await fetch(`${apiBase}/opa/sct/import?apply=1`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(plan.body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xSct.errImport'));
      setPlan(null);
      setResult(data);
      /* The refused rows come back as a workbook and are handed over immediately. Leaving them behind
         a second click is how a person walks away believing everything went in. */
      if (data.duplicateFileBase64) {
        const bin = atob(data.duplicateFileBase64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
        const url = URL.createObjectURL(new Blob([bytes],
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
        const a = document.createElement('a');
        a.href = url; a.download = data.duplicateFileName || 'SCT_duplicates.xlsx';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
      await load(data.season);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.sap} ${r.styleName} ${r.colorway} ${r.line} ${r.category} ${r.docNo}`
      .toLowerCase().includes(q));
  }, [rows, search]);

  const shown = useMemo(() => {
    const kept = Object.keys(filters).length
      ? searched.filter((r) => rowPassesColumnFilters(r, filters, GETTERS))
      : searched;
    return sort.key ? sortRows(kept, sort, GETTERS) : kept;
  }, [searched, filters, sort]);

  const narrowed = !!search.trim() || !!sort.key || !!Object.keys(filters).length;
  const clearAll = () => {
    setSearch(''); setSort({ key: '', dir: 'asc' }); setFilters({}); setOpenFilter(null);
  };

  const th = (column) => ({
    label: labelOf(column.key, column.label),
    sortKey: column.key,
    sort,
    className: `${column.num ? 'num' : (column.cls || '')}`
      + (FROZEN[column.key] ? ` sticky-col${column.key === 'styleName' ? ' last-sticky' : ''}` : ''),
    style: FROZEN[column.key]
      ? { left: frozenLeft[FROZEN_KEYS.indexOf(column.key) + 1], width: FROZEN[column.key] }
      : undefined,
    onSort: (key, dir) => setSort((prev) => ({
      key, dir: dir || (prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'),
    })),
    filterValues: openFilter?.key === column.key
      ? distinctColumnValues(searched, column.key, filters, GETTERS) : undefined,
    filterSelected: filters[column.key],
    filterOpen: openFilter?.key === column.key,
    filterAnchor: openFilter?.key === column.key ? openFilter.anchor : null,
    onOpenFilter: (key, anchor) => setOpenFilter({ key, anchor }),
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (key, picked, all) => {
      setFilters((prev) => {
        const next = { ...prev };
        if (!picked.length || picked.length === all.length) delete next[key];
        else next[key] = picked;
        return next;
      });
      setOpenFilter(null);
    },
  });


  return (
    <div className="opa sct-sum">
      {!!error && <div className="sp-error">{error}</div>}

      <div className="opa-bar">
        <input
          className="sp-input opa-search"
          type="search"
          placeholder={t('xSct.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="sp-input"
          value={season}
          onChange={(e) => { setSeason(e.target.value); load(e.target.value); }}
          disabled={!seasons.length}
        >
          {!seasons.length && <option value="">{t('xSct.noSeason')}</option>}
          {seasons.map((sn) => (
            <option key={sn.season} value={sn.season}>{t('xSct.seasonOpt', { season: sn.season, n: sn.rows })}</option>
          ))}
        </select>
        {/* A POST, so the rows ON SCREEN travel with it — an export that ignored the search and the
            funnels would answer a different question from the one being looked at. */}
        <button type="button" className="sp-btn" data-tour="sct-export" onClick={exportXlsx} disabled={busy || !shown.length}>
          <FileDown size={14} /> {t('xSct.exportXlsx')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx"
          style={{ display: 'none' }}
          onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }}
        />
        <button type="button" className="sp-btn" data-tour="sct-import" onClick={() => fileRef.current?.click()} disabled={busy}>
          <Upload size={14} /> {t('xSct.importXlsx')}
        </button>
        {/* The blank workbook the importer accepts. Built server-side from the same header map the
            parser reads, so nobody has to work the format out from a rejection. */}
        <a className="sp-btn" href={`${apiBase}/opa/sct/template`}>
          <FileDown size={14} /> {t('xSct.importFormat')}
        </a>
        <button type="button" className="sp-btn" onClick={() => load(season)} disabled={busy}>
          <RefreshCw size={14} /> {busy ? t('xSct.loading') : t('xSct.refresh')}
        </button>
        {narrowed && (
          <button type="button" className="sp-btn opa-clear" onClick={clearAll}>
            <FilterX size={14} /> {t('xSct.clear')}
          </button>
        )}
      </div>

      {/* What the file holds, before anything is written. The duplicate list is the point of the
          dialog: a season's cycle times are somebody's month of work, and an import that silently
          replaced them would be very hard to notice and impossible to undo. */}
      {!!plan && (
        <div className="sct-import-plan">
          <div className="sct-import-head">
            <strong>{plan.body.fileName}</strong>
            <span>
              {t('xSct.planMeta', {
                count: (plan.seasons || [plan.season]).length,
                seasons: (plan.seasons || [plan.season]).join(', '),
                sheet: plan.sheet,
                total: plan.total,
              })}
            </span>
          </div>
          <p className="sct-import-line">
            {fillParts(t('xSct.willImport', { n: marker('n') }), { n: <strong>{plan.willImport}</strong> })}
            {!!plan.duplicates.length && (
              <>
                {' '}<AlertTriangle size={13} />
                {' '}{t('xSct.dupes', { n: plan.duplicates.length })}
              </>
            )}
          </p>
          {!!plan.duplicates.length && (
            <div className="sct-dupe-list">
              {/* The season is on each chip: a code is only a duplicate OF A SEASON now, and a list
                  of bare numbers would not say which one it collided with. */}
              {plan.duplicates.slice(0, 40).map((d) => (
                <span key={`${d.season}-${d.sap}`} className="sct-dupe">
                  {d.season ? `${d.sap} · ${d.season}` : d.sap}
                </span>
              ))}
              {plan.duplicates.length > 40 && (
                <span className="sct-dupe more">{t('xSct.more', { n: plan.duplicates.length - 40 })}</span>
              )}
            </div>
          )}
          <div className="sct-import-btns">
            <button type="button" className="sp-btn primary" onClick={runImport}
                    disabled={busy || !plan.willImport}>
              {busy ? t('xSct.importing') : t('xSct.importN', { n: plan.willImport })}
            </button>
            <button type="button" className="sp-btn" onClick={() => setPlan(null)} disabled={busy}>
              {t('xSct.cancel')}
            </button>
          </div>
        </div>
      )}

      {!!result && (
        <div className="sct-import-done">
          {(result.importedBySeason || []).length
            ? t('xSct.importedInto', {
              n: result.imported,
              seasons: result.importedBySeason.map((x) => `${x.season} (${x.n})`).join(', '),
            })
            : t('xSct.imported', { n: result.imported })}
          {!!result.skipped && <>{' '}{fillParts(t('xSct.skippedDl', { n: result.skipped, file: marker('file') }),
            { file: <strong>{result.duplicateFileName}</strong> })}</>}
          <button type="button" className="sp-mini-btn" onClick={() => setResult(null)}>{t('xSct.dismiss')}</button>
        </div>
      )}

      {/* Edit changes only what a person measured. The identity block is read-only and shown anyway,
          because a dialog full of unlabelled numbers is how the wrong row gets edited. */}
      {!!edit && (
        <div className="st-modal-overlay sct-over" role="dialog" aria-modal="true">
          <div className="st-modal sct-edit">
            <div className="st-modal-head">
              <h3>{`${edit.row.sap} — ${edit.row.styleName || ''}`}{edit.view && <span className="pt-view-tag">{t('xSct.viewOnly')}</span>}</h3>
              <button type="button" className="st-modal-close" onClick={() => setEdit(null)}>&times;</button>
            </div>
            <div className="st-modal-body">
              <div className="sct-edit-id">
                {/* SAP code and Style name first: they are what a person recognises the row by, and
                    the dialog was missing both (Max, 2026-09-09). */}
                <span><b>{t('xSct.idSap')}</b> {edit.row.sap}</span>
                <span><b>{t('xSct.idStyle')}</b> {edit.row.styleName || '—'}</span>
                <span><b>{t('xSct.idSeason')}</b> {edit.row.season}</span>
                <span><b>{t('xSct.idLine')}</b> {edit.row.line || '—'}</span>
                <span><b>{t('xSct.idCategory')}</b> {edit.row.category || '—'}</span>
                <span><b>{t('xSct.idColorway')}</b> {edit.row.colorway || '—'}</span>
                <span><b>{t('xSct.idFrameColor')}</b> {edit.row.frameColor || '—'}</span>
                <span className="sct-edit-note">{t('xSct.idNote')}</span>
              </div>
              <fieldset className="pt-ro-fieldset" disabled={!!edit.view}>
              <div className="sct-edit-grid">
                {/* TYPE TO FIND IT (Max, 2026-09-09: "user có thể gõ để tìm ĐÚNG PHIẾU"). A native
                    <select> of 128 documents can only be scrolled, and "the right one" is usually
                    known by its style name rather than its position in a list. So: an input that
                    filters, over the number AND the name AND the variant, because a person looking
                    for the Chair One document types "chair", not "OPA-000001".
                    Still a picker underneath — typing alone changes nothing until a row is chosen,
                    so a half-typed word cannot be saved as a document number. */}
                <div className="sct-edit-f sct-edit-opa">
                  <span>{t('xSct.opa')}</span>
                  {(() => {
                    const chosen = opaDocs.find((d) => d.docNo === edit.opaDocNo);
                    const q = String(opaQuery ?? '').trim().toLowerCase();
                    const matches = !q ? opaDocs
                      : opaDocs.filter((d) => d.label.toLowerCase().includes(q));
                    return (
                      <div className="sct-opa-pick">
                        <input
                          value={opaQuery ?? (chosen ? chosen.label
                            : (edit.opaDocNo ? t('xSct.noLongerActive', { doc: edit.opaDocNo }) : ''))}
                          placeholder={t('xSct.opaPlaceholder')}
                          onChange={(e) => setOpaQuery(e.target.value)}
                          onFocus={() => setOpaQuery('')}
                        />
                        {edit.opaDocNo && opaQuery === null && (
                          <button
                            type="button"
                            className="sct-opa-clear"
                            title={t('xSct.clear')}
                            onClick={() => setEdit({ ...edit, opaDocNo: '' })}
                          >
                            &times;
                          </button>
                        )}
                        {opaQuery !== null && (
                          <>
                            {/* The scrim closes the list on any click outside it — without one the
                                list stays open behind whatever is clicked next. */}
                            <div className="sct-opa-scrim" onClick={() => setOpaQuery(null)} />
                            <div className="sct-opa-list">
                              <button
                                type="button"
                                className="sct-opa-opt none"
                                onClick={() => { setEdit({ ...edit, opaDocNo: '' }); setOpaQuery(null); }}
                              >
                                {t('xSct.none')}
                              </button>
                              {matches.slice(0, 60).map((d) => (
                                <button
                                  type="button"
                                  key={d.docNo}
                                  className={`sct-opa-opt${d.docNo === edit.opaDocNo ? ' on' : ''}`}
                                  onClick={() => {
                                    setEdit({ ...edit, opaDocNo: d.docNo });
                                    setOpaQuery(null);
                                  }}
                                >
                                  {d.label}
                                </button>
                              ))}
                              {!matches.length && (
                                <div className="sct-opa-empty">{t('xSct.noDocMatch', { q: opaQuery })}</div>
                              )}
                              {matches.length > 60 && (
                                <div className="sct-opa-empty">
                                  {t('xSct.moreDocs', { n: matches.length - 60 })}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {EDITABLE.map((f) => (
                  <label key={f.key} className="sct-edit-f">
                    <span>{f.label}{f.pct ? ' (%)' : ''}</span>
                    <input
                      type="number"
                      step={f.pct ? '1' : '0.01'}
                      value={edit.form[f.key]}
                      placeholder={t('xSct.notMeasured')}
                      onChange={(e) => setEdit({
                        ...edit, form: { ...edit.form, [f.key]: e.target.value },
                      })}
                    />
                  </label>
                ))}
              </div>
              </fieldset>
            </div>
            <div className="st-modal-foot">
              {!edit.view && (
              <button type="button" className="sp-btn primary" onClick={saveEdit} disabled={busy}>
                {busy ? t('xSct.saving') : t('xSct.save')}
              </button>
              )}
              <button type="button" className="sp-btn" onClick={() => setEdit(null)} disabled={busy}>
                {edit.view ? t('xSct.close') : t('xSct.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {!!drop && (
        <div className="st-modal-overlay sct-over" role="dialog" aria-modal="true">
          <div className="st-modal sct-drop">
            <div className="st-modal-head"><h3>{t('xSct.dropTitle')}</h3></div>
            <div className="st-modal-body">
              <p>
                {t('xSct.dropWhat', { sap: drop.sap, style: drop.styleName || '', season: drop.season })}
              </p>
              <p className="sct-drop-note">
                {t('xSct.dropNote')}
              </p>
            </div>
            <div className="st-modal-foot">
              <button type="button" className="sp-btn danger" onClick={doDelete} disabled={busy}>
                {busy ? t('xSct.deleting') : t('xSct.delete')}
              </button>
              <button type="button" className="sp-btn" onClick={() => setDrop(null)} disabled={busy}>
                {t('xSct.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 18 columns: the table scrolls inside its own box. Letting the PAGE scroll sideways instead
          takes the sidebar and the toolbar with it. */}
      <div className="sct-scroll">
      <table className="sp-table sct-table" ref={tableRef}>
        {/* See the OPA list: .sticky-col's `max-width: 0` needs a column width to honour. */}
        <colgroup>
          <col style={{ width: 46 }} />
          {COLUMNS.map((c) => <col key={c.key} style={{ width: COL_W(c.key) }} />)}
          {/* Sized from the two buttons it holds. A button rendered but not counted here makes the
              width wrong and the last one hangs outside the frozen column — that has cost this
              codebase a session before. */}
          <col style={{ width: ACTION_W }} />
        </colgroup>
        <thead>
          {/* The workbook's upper tier. Built from GROUPS rather than written out, so a column added
              between them cannot leave the banners spanning the wrong cells. */}
          <tr className="sct-group-row">
            {(() => {
              /* A run of empty cells is ONE cell (Max, 2026-09-08: "tầng 1 có nhiều ô trống liên tục
                 thì nên merge lại"). A frozen column cannot join a run — each one carries its own
                 sticky left offset, and a merged cell has only one — so the freeze block stays as
                 individual blanks and only the free columns between the banners collapse. */
              const cells = [];
              let i = 0;
              let run = 0;
              const flush = () => {
                if (!run) return;
                cells.push(<th key={`gap${i}`} colSpan={run} className="sct-group-gap" />);
                run = 0;
              };
              cells.push(<th key="no" className="sticky-col sct-no" style={{ left: frozenLeft[0] }} />);
              const cols = COLUMNS;
              while (i < cols.length) {
                const c = cols[i];
                const g = GROUPS.find((x) => x.from === c.key);
                if (g) {
                  flush();
                  cells.push(<th key={g.from} colSpan={g.span} className="sct-group">{GROUP_LABEL[g.from] || g.label}</th>);
                  i += g.span;
                } else if (FROZEN[c.key]) {
                  flush();
                  cells.push(<th
                    key={c.key}
                    className={`sticky-col${c.key === 'styleName' ? ' last-sticky' : ''}`}
                    style={{ left: frozenLeft[FROZEN_KEYS.indexOf(c.key) + 1], width: FROZEN[c.key] }}
                  />);
                  i += 1;
                } else {
                  run += 1;
                  i += 1;
                }
              }
              flush();
              cells.push(<th key="act" className="sticky-right sct-act" />);
              return cells;
            })()}
          </tr>
          <tr>
            {/* Numbers what is ON SCREEN, so it renumbers with the sort and the filter. */}
            <th className="sticky-col sct-no" style={{ left: frozenLeft[0] }}>{t('xSct.no')}</th>
            {COLUMNS.map((c) => <SortTh key={c.key} {...th(c)} />)}
            <th className="sticky-right sct-act">{t('xSct.action')}</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={`${r.season}-${r.sap}`}>
              <td className="num sticky-col sct-no" style={{ left: frozenLeft[0] }}>{i + 1}</td>
              {COLUMNS.map((c) => {
                const frozen = FROZEN[c.key];
                const cls = [
                  c.num ? 'num' : (c.cls || ''),
                  c.key === 'sap' ? 'sct-code' : '',
                  c.key === 'season' ? 'sct-season' : '',
                  c.key === 'sct' ? 'sct-total' : '',
                  // The two Σ columns are sums of the cells beside them, not measurements — shaded
                  // so the eye reads them as totals rather than as another cycle time.
                  // `sct-sum` is already the PAGE's class — one name for two things is how a rule
                  // written for the panel ends up painting a cell.
                  c.derived ? 'sct-subtotal' : '',
                  frozen ? 'sticky-col' : '',
                  frozen && c.key === 'styleName' ? 'last-sticky' : '',
                ].filter(Boolean).join(' ');
                const style = frozen
                  ? { left: frozenLeft[FROZEN_KEYS.indexOf(c.key) + 1] } : undefined;
                let body;
                if (c.key === 'docNo') {
                  /* A reference, not a link into a document: an imported row carries the OPA NUMBER
                     the measurement came from, not the document's internal id. */
                  body = r.docNo
                    ? <span className="sp-mini-btn is-flat">
                        <ExternalLink size={12} /> {String(r.docNo).replace('OPA-', '')}
                      </span>
                    : <span className="sct-muted">—</span>;
                } else if (c.key === 'styleName') {
                  body = <strong>{r.styleName}</strong>;
                } else if (c.key === 'efficiency') {
                  body = pct(r.efficiency);
                } else if (c.num) {
                  body = n2(r[c.key]);
                } else {
                  body = r[c.key] || '—';
                }
                const title = ['styleName', 'colorway', 'frameColor', 'category', 'line']
                  .includes(c.key) ? (r[c.key] || '') : undefined;
                return <td key={c.key} className={cls} style={style} title={title}>{body}</td>;
              })}
              <td className="sticky-right sct-act">
                <button type="button" className="sp-mini-btn" onClick={() => openEdit(r, true)} title={t('xSct.viewTitle')}>
                  <Eye size={12} /> {t('xSct.view')}
                </button>
                <button type="button" className="sp-mini-btn" onClick={() => openEdit(r)}>
                  <Pencil size={12} /> {t('xSct.edit')}
                </button>
                <button type="button" className="sp-mini-btn danger" onClick={() => setDrop(r)}>
                  <Trash2 size={12} /> {t('xSct.delete')}
                </button>
              </td>
            </tr>
          ))}
          {!shown.length && (
            <tr><td className="sp-empty" colSpan={COLUMNS.length + 2}>
              {busy ? t('xSct.loading') : t('xSct.empty')}
            </td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}
